(() => {
const REWARD_GIFTS = [
  { key: 'lanyard', thresholdEur: 5500, variantIds: [56345444942159], defaultVariantId: 56345444942159 },
  { key: 'grip', thresholdEur: 8500, variantIds: [56392242135375, 56345695748431, 56345414467919], defaultVariantId: 56392242135375 }
];
const GIFT_PROPERTY = '_cherries_free_gift';
const giftGroup = item => REWARD_GIFTS.find(gift => gift.variantIds.includes(Number(item.variant_id)));
const isAutoGift = item => Boolean(giftGroup(item)) && String(item.properties?.[GIFT_PROPERTY]) === 'true';
const isGiftWrap = item => item.handle === 'gift-wrapping';
const isLegacyGift = item => String(item.properties?._cherries_free_screen_protector) === 'true';
const finalLinePrice = item => Number(item.final_line_price ?? item.line_price ?? 0);

function calculateRewards(cart, country, rate) {
  const currency = cart.currency || 'EUR';
  const exchangeRate = currency === 'EUR' ? 1 : Number(rate);
  const ready = Number.isFinite(exchangeRate) && exchangeRate > 0;
  const convert = amount => ready ? Math.round(amount * exchangeRate) : null;
  const shippingThreshold = convert(country === 'NL' || country === 'BE' ? 4000 : 8000);
  // Never let an automatically added gift count towards earning itself.
  const paidGiftValue = cart.items.reduce((sum, item) => sum + (isAutoGift(item) || isLegacyGift(item) ? finalLinePrice(item) : 0), 0);
  const shippingSpend = Math.max(0, Number(cart.total_price || 0) - paidGiftValue);
  const giftSpend = cart.items.reduce((sum, item) => {
    if (isAutoGift(item) || isLegacyGift(item) || isGiftWrap(item)) return sum;
    // Shopify Buy X Get Y excludes items already receiving a product discount.
    if (item.line_level_discount_allocations?.some(allocation => Number(allocation.amount) > 0)) return sum;
    if (giftGroup(item) && finalLinePrice(item) === 0) return sum;
    return sum + Number(item.original_line_price ?? item.line_price ?? finalLinePrice(item));
  }, 0);
  const stages = [
    { key: 'shipping', threshold: shippingThreshold, amount: shippingSpend },
    ...REWARD_GIFTS.map(gift => ({ key: gift.key, threshold: convert(gift.thresholdEur), amount: giftSpend }))
  ].map(stage => ({ ...stage, unlocked: ready && stage.amount >= stage.threshold }))
    .sort((a, b) => a.threshold - b.threshold);
  const nextIndex = stages.findIndex(stage => !stage.unlocked);
  const next = nextIndex < 0 ? null : stages[nextIndex];
  let progress = ready && !next ? 100 : 0;
  if (ready && next) {
    const previousThreshold = nextIndex === 0 ? 0 : stages[nextIndex - 1].threshold;
    const part = Math.max(0, Math.min(1, (next.amount - previousThreshold) / (next.threshold - previousThreshold)));
    progress = (nextIndex + part) / stages.length * 100;
  }
  return { ready, currency, exchangeRate, shippingSpend, giftSpend, stages, next, progress };
}

function planGifts(cart, state, selectedVariants = {}) {
  const updates = {};
  const additions = [];
  const hasPaidProducts = cart.items.some(item => !isGiftWrap(item) && !isAutoGift(item) && !isLegacyGift(item));
  cart.items.forEach(item => {
    if (isLegacyGift(item)) updates[item.key] = 0;
    if (isGiftWrap(item) && (!hasPaidProducts || Number(item.quantity) !== 1)) updates[item.key] = hasPaidProducts ? 1 : 0;
  });
  if (!state.ready) return { updates, additions };
  REWARD_GIFTS.forEach(group => {
    const stage = state.stages.find(stage => stage.key === group.key);
    const requested = Number(selectedVariants[group.key]);
    const variantId = group.variantIds.includes(requested) ? requested : group.defaultVariantId;
    const owned = cart.items.filter(item => isAutoGift(item) && giftGroup(item).key === group.key);
    // A customer's own item already discounted to free also fulfils the tier.
    const existingFree = cart.items.find(item => !isAutoGift(item) && giftGroup(item)?.key === group.key && finalLinePrice(item) === 0);
    const keep = stage.unlocked && !existingFree ? owned.find(item => Number(item.variant_id) === variantId) : null;
    owned.forEach(item => {
      if (item !== keep) updates[item.key] = 0;
      else if (Number(item.quantity) !== 1) updates[item.key] = 1;
    });
    if (stage.unlocked && !keep && !existingFree) {
      additions.push({ id: variantId, quantity: 1, properties: { [GIFT_PROPERTY]: 'true', _cherries_gift_tier: group.key } });
    }
  });
  return { updates, additions };
}


  async function refreshVisibleCart() {
    // Gift changes can alter Shopify line keys and discount allocations.
    // Reload the full cart after synchronization so no older section response
    // can put a removed gift back on screen.
    if (document.querySelector('cart-items')) {
      window.location.replace(window.location.href);
      return;
    }
    const cartViews = [...document.querySelectorAll('cart-items, cart-drawer-items')];
    await Promise.allSettled(cartViews.filter(view => typeof view.onCartUpdate === 'function').map(view => view.onCartUpdate()));
    // The final automatic-gift update must refresh the header on drawer pages too.
    const footer = document.getElementById('main-cart-footer');
    const sectionIds = ['cart-icon-bubble'];
    if (footer?.dataset.id) sectionIds.push(footer.dataset.id);
    try {
      const response = await fetch((window.routes?.cart_url || '/cart') + '?sections=' + encodeURIComponent(sectionIds.join(',')), { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart sections could not be refreshed');
      const sections = await response.json();
      const parser = new DOMParser();
      if (footer?.dataset.id && sections[footer.dataset.id]) {
        const freshFooter = parser.parseFromString(sections[footer.dataset.id], 'text/html').getElementById('main-cart-footer');
        if (freshFooter) footer.innerHTML = freshFooter.innerHTML;
      }
      const bubble = document.getElementById('cart-icon-bubble');
      const freshBubble = sections['cart-icon-bubble'] && parser.parseFromString(sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
      if (bubble && freshBubble) bubble.innerHTML = freshBubble.innerHTML;
    } catch (error) { console.warn('[Cherries rewards] Cart display refresh failed:', error); }
  }


  let syncing = false;
  let refreshTimer;
  let refreshAgain = false;
  let cartCurrency = window.Shopify?.currency?.active || 'EUR';
  const failedGifts = new Map();
  const selectedVariants = { lanyard: 56345444942159, grip: 56392242135375 };
  try {
    const saved = Number(localStorage.getItem('cherries_gift_variant_id'));
    if (REWARD_GIFTS[1].variantIds.includes(saved)) selectedVariants.grip = saved;
  } catch (error) {}
  const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency', currency:cartCurrency}).format(Math.max(0,cents)/100);
  const getState = cart => calculateRewards(cart, document.querySelector('[data-cherries-rewards]')?.dataset.deliveryCountry || 'NL', window.Shopify?.currency?.rate);
  const fingerprint = cart => JSON.stringify([cart.currency,document.querySelector('[data-cherries-rewards]')?.dataset.deliveryCountry,cart.items.filter(item=>!isAutoGift(item)&&!isLegacyGift(item)).map(item=>[item.variant_id,item.quantity,item.original_line_price,item.properties])]);

  function render(cart) {
    cartCurrency = cart.currency || 'EUR';
    const state = getState(cart);
    // Native quantity updates can include a gift before it is removed. Always
    // reconcile the full-cart total with the final authoritative cart response.
    document.querySelectorAll('#main-cart-footer .totals__total-value').forEach(total => {
      const formatted = money(Number(cart.total_price || 0)) + ' ' + cartCurrency;
      if (total.textContent !== formatted) total.textContent = formatted;
    });
    if (state.ready) document.querySelectorAll('[data-cherries-gift-promo]').forEach(element => { element.textContent = money(Math.round(Number(element.dataset.cherriesGiftPromo) * state.exchangeRate)); });
    const dutch = document.documentElement.lang.split('-')[0] === 'nl';
    // Use the final cart response for the header, including automatic gifts.
    const icon = document.getElementById('cart-icon-bubble');
    if (icon) {
      const count = Number(cart.item_count || 0);
      let badge = icon.querySelector('.cart-count-bubble');
      if (!count) badge?.remove();
      else {
        if (!badge) { badge = document.createElement('div'); badge.className = 'cart-count-bubble'; icon.appendChild(badge); }
        const visible = document.createElement('span'); visible.setAttribute('aria-hidden', 'true'); visible.textContent = count < 100 ? String(count) : '';
        const accessible = document.createElement('span'); accessible.className = 'visually-hidden'; accessible.textContent = dutch ? count + (count === 1 ? ' artikel' : ' artikelen') : count + (count === 1 ? ' item' : ' items');
        if (badge.textContent !== visible.textContent + accessible.textContent) badge.replaceChildren(visible, accessible);
      }
      const svgCount = icon.querySelector('[data-hook="items-count"]');
      if (svgCount && svgCount.textContent !== String(count)) svgCount.textContent = String(count);
    }

    const labels = dutch ? {shipping:'Gratis verzending',lanyard:'Gratis Wrist Loop',grip:'2 gratis cadeaus'} : {shipping:'Free shipping',lanyard:'Free Wrist Loop',grip:'2 free gifts'};
    const nextNames = dutch ? {shipping:'gratis verzending',lanyard:'een gratis Wrist Loop',grip:'2 gratis cadeaus'} : {shipping:'free shipping',lanyard:'a free Wrist Loop',grip:'2 free gifts'};
    const failed = REWARD_GIFTS.some(group=>state.stages.find(stage=>stage.key===group.key)?.unlocked && failedGifts.get(group.key)===fingerprint(cart));
    const summary = !state.ready
      ? (dutch?'Je voordelen worden bij het afrekenen bevestigd.':'Your rewards will be confirmed at checkout.')
      : failed ? (dutch?'Je cadeau is tijdelijk niet beschikbaar.':'Your gift is temporarily unavailable.')
      : !state.next ? (dutch?'Alle voordelen ontgrendeld!':'All rewards unlocked!')
      : (dutch?`Nog ${money(state.next.threshold-state.next.amount)} voor ${nextNames[state.next.key]}`:`Only ${money(state.next.threshold-state.next.amount)} away from ${nextNames[state.next.key]}`);
    document.querySelectorAll('[data-cherries-rewards]').forEach(box=>{
      const summaryElement=box.querySelector('[data-rewards-summary]');
      if(summaryElement) summaryElement.textContent=summary;
      const fill=box.querySelector('[data-rewards-fill]');
      if(fill) fill.style.width=`${state.progress}%`;
      const headline=box.querySelector('[data-rewards-headline]');
      const unlocked=state.stages.filter(stage=>stage.unlocked).length;
      if(headline) headline.textContent=dutch?`${unlocked} van 3 voordelen ontgrendeld`:`${unlocked} of 3 rewards unlocked`;
      const status=[];
      state.stages.forEach((stage,index)=>{
        const marker=box.querySelectorAll('[data-reward-marker]')[index];
        if(marker) {marker.classList.toggle('is-unlocked',stage.unlocked);marker.dataset.rewardKind=stage.key;}
        const label=box.querySelectorAll('[data-reward-label]')[index];
        if(label) label.textContent=labels[stage.key];
        const tier=box.querySelector(`[data-reward-tier="${stage.key}"]`);
        if(tier) tier.classList.toggle('is-unlocked',stage.unlocked);
        const message=box.querySelector(`[data-reward-message="${stage.key}"]`);
        let text;
        if(!state.ready) text=dutch?'Wordt berekend…':'Calculating…';
        else if(stage.key==='shipping') text=stage.unlocked?(dutch?'Je krijgt GRATIS verzending!':"You've unlocked FREE shipping!"):(dutch?`Voeg nog ${money(stage.threshold-stage.amount)} toe voor gratis verzending.`:`Add ${money(stage.threshold-stage.amount)} more for free shipping.`);
        else if(!stage.unlocked) text=dutch?`Besteed nog ${money(stage.threshold-stage.amount)} voor ${nextNames[stage.key]}.`:`Spend ${money(stage.threshold-stage.amount)} more for ${nextNames[stage.key]}.`;
        else if(failedGifts.get(stage.key)===fingerprint(cart)) text=dutch?'Dit cadeau is tijdelijk niet beschikbaar.':'This gift is temporarily unavailable.';
        else {
          const hasFreeGift=cart.items.some(item=>giftGroup(item)?.key===stage.key&&finalLinePrice(item)===0);
          text=hasFreeGift?(stage.key==='lanyard'?(dutch?'Gratis Wrist Loop toegevoegd.':'Free Wrist Loop added.'):(dutch?'Gratis suction grip toegevoegd; je Wrist Loop blijft ook gratis.':'Free suction grip added; your Wrist Loop is also free.')):(dutch?'Je gratis cadeau wordt toegevoegd…':'Adding your free gift…');
        }
        if(message) message.textContent=text;
        status.push(`${labels[stage.key]}: ${text}`);
      });
      const accessible=box.querySelector('[data-rewards-accessible-summary]');
      if(accessible) accessible.textContent=status.join(' ');
    });
  }

  async function getCart() {
    const response=await fetch(`${window.routes?.cart_url||'/cart'}.js`,{headers:{Accept:'application/json'}});
    if(!response.ok) throw new Error('Unable to load cart');
    return response.json();
  }

  async function postCart(endpoint,payload) {
    const response=await fetch(endpoint,{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify(payload)});
    const result=await response.json();
    if(!response.ok||result.status) throw new Error(result.description||'Unable to update gift');
    return result;
  }

  async function syncGift(cart) {
    if(syncing) return cart;
    const plan=planGifts(cart,getState(cart),selectedVariants);
    const cartKey=fingerprint(cart);
    const additions=plan.additions.filter(item=>failedGifts.get(item.properties._cherries_gift_tier)!==cartKey);
    if(!Object.keys(plan.updates).length&&!additions.length) return cart;
    syncing=true;
    let changed=false;
    try {
      if(Object.keys(plan.updates).length) {
        await postCart(window.routes?.cart_update_url||'/cart/update.js',{updates:plan.updates});
        cart=await getCart();changed=true;
      }
      for(const item of additions) {
        try {
          await postCart(window.routes?.cart_add_url||'/cart/add.js',{items:[item]});
          changed=true;
        } catch(error) {
          failedGifts.set(item.properties._cherries_gift_tier,cartKey);
          console.warn('[Cherries rewards] Gift unavailable:',error);
        }
      }
      cart=await getCart();
      // A gift must be discounted by Shopify itself, including at checkout.
      // Remove any automatic gift which Shopify would otherwise charge for.
      const charged={};
      cart.items.forEach(item=>{
        if(isAutoGift(item)&&finalLinePrice(item)>0) {
          charged[item.key]=0;
          failedGifts.set(giftGroup(item).key,cartKey);
        }
      });
      if(Object.keys(charged).length) {
        await postCart(window.routes?.cart_update_url||'/cart/update.js',{updates:charged});
        cart=await getCart();changed=true;
      }
      if(changed) await refreshVisibleCart();
      return cart;
    } catch(error) {
      console.warn('[Cherries rewards] Gift sync failed:',error);
      return await getCart().catch(()=>cart);
    } finally {
      syncing=false;
      if(refreshAgain) {refreshAgain=false;refresh();}
    }
  }

  function refresh() {
    clearTimeout(refreshTimer);
    refreshTimer=setTimeout(async()=>{
      if(syncing) {refreshAgain=true;return;}
      try {
        let cart=await getCart();
        render(cart);
        cart=await syncGift(cart);
        render(cart);
      } catch(error) {console.warn('[Cherries rewards] Refresh failed:',error);}
    },120);
  }

  document.addEventListener('cherries:gift-color-change',event=>{
    const variantId=Number(event.detail?.variantId);
    if(REWARD_GIFTS[1].variantIds.includes(variantId)) {selectedVariants.grip=variantId;refresh();}
  });
  const originalFetch=window.fetch.bind(window);
  window.fetch=async(...args)=>{
    const response=await originalFetch(...args);
    const url=String(args[0]?.url||args[0]||'');
    if(!syncing&&/\/cart\/(add|change|update|clear)(\.js)?/.test(url)) refresh();
    return response;
  };
  function watchCartChanges() {
    if(typeof subscribe==='function'&&typeof PUB_SUB_EVENTS!=='undefined') subscribe(PUB_SUB_EVENTS.cartUpdate,refresh);
    const drawer=document.querySelector('cart-drawer');
    if(drawer) new MutationObserver(mutations=>{
      if(mutations.some(mutation=>[...mutation.addedNodes].some(node=>node.nodeType===1&&(node.matches('[data-cherries-rewards]')||node.querySelector('[data-cherries-rewards]'))))) refresh();
    }).observe(drawer,{childList:true,subtree:true});
    refresh();
  }
  if(document.readyState==='loading') document.addEventListener('DOMContentLoaded',watchCartChanges,{once:true});
  else watchCartChanges();
  // Restore rewards after browser back/forward cache and native cart changes.
  window.addEventListener('pageshow', refresh);
  const refreshAfterNativeCartChange = event => {
    const target = event.target.closest('cart-remove-button, quantity-input button, quantity-input input');
    if (!target || !target.closest('cart-items, cart-drawer-items')) return;
    setTimeout(refresh, 350);
    setTimeout(refresh, 1200);
  };
  document.addEventListener('click', refreshAfterNativeCartChange, true);
  document.addEventListener('change', refreshAfterNativeCartChange, true);
  document.addEventListener('cart:updated',refresh);
  document.addEventListener('cartUpdate',refresh);

})();

(() => {
  document.addEventListener('change', async event => {
    const checkbox = event.target.closest('[data-cherries-gift-wrap]');
    if (!checkbox) return;
    const drawer = checkbox.closest('cart-drawer');
    const requested = checkbox.checked;
    const error = checkbox.closest('.cc-gift-wrap').querySelector('[data-gift-wrap-error]');
    checkbox.disabled = true;
    error.hidden = true;
    try {
      const sections = drawer.getSectionsToRender().map(section => section.id);
      const payload = { sections, sections_url: window.location.pathname };
      let endpoint;
      if (requested) {
        endpoint = window.routes.cart_add_url;
        payload.items = [{ id: Number(checkbox.dataset.variantId), quantity: 1, properties: { '_Cherries gift wrapping': 'true' } }];
      } else {
        endpoint = window.routes.cart_change_url;
        payload.id = checkbox.dataset.lineKey;
        payload.quantity = 0;
      }
      const result = await fetch(endpoint, { method:'POST', headers:{'Content-Type':'application/json', Accept:'application/json'}, body:JSON.stringify(payload) });
      const cart = await result.json();
      if (!result.ok || cart.status) throw new Error(cart.description || 'Gift wrapping could not be updated. Please try again.');
      drawer.setActiveElement(checkbox);
      drawer.renderContents(cart);
      if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source:'cherries-gift-wrap', cartData:cart });
    } catch (issue) {
      checkbox.checked = !requested;
      error.textContent = issue.message;
      error.hidden = false;
    } finally { checkbox.disabled = false; }
  });
})();
